/**
 * UK Workboat Code Edition 3 - WBC3 Compliant Drill Requirements
 * 
 * CRITICAL COMPLIANCE NOTE:
 * Fire and Abandon Ship drills are MANDATORY MONTHLY per SI 1999/2722 Regulation 8(2)
 * NO exceptions for crew size. Each crew member must participate at least once per month.
 * 
 * Any deviation from monthly fire/abandon ship drills requires written MCA approval.
 * 
 * MOB drills: "regularly routinely" per WBC3 14.11.2 - industry practice is quarterly (90 days)
 * Other drills: Based on risk assessment and regulatory requirements
 */

import { COMPLIANCE_CONSTANTS } from './complianceConstants';
import { determineRegulatoryFramework, getDrillRequirementByFramework } from './regulatoryFramework';

/**
 * Normalize vessel type for consistent comparison
 * Ensures all vessel type comparisons work regardless of casing
 */
const normalizeVesselType = (type) => {
  return (type || '').toLowerCase().trim();
};

// Core drill types per WBC3 requirements
export const DRILL_TYPES = {
  FIRE: 'fire',
  ABANDON_SHIP: 'abandon_ship',
  MOB: 'mob',
  POLLUTION_RESPONSE: 'pollution_response',
  GMDSS_DSC: 'gmdss_dsc',
  MEDICAL_EMERGENCY: 'medical_emergency',
  FLOODING: 'flooding',
  STEERING_NAV_FAIL: 'steering_nav_fail',
  ELECTRICAL_BLACKOUT: 'electrical_blackout',
  ENCLOSED_SPACE_ENTRY: 'enclosed_space_entry',
  TOWING_EMERGENCY: 'towing_emergency',
  COLLISION: 'collision',
  GROUNDING: 'grounding'
};

// Compliance levels per regulatory guidance
export const COMPLIANCE_LEVELS = {
  REQUIRED: 'Required',      // Legally mandated under WBC3 or ISM
  RECOMMENDED: 'Recommended', // Best practice or commonly required by SMS
  OPTIONAL: 'Optional'       // Operator-defined or based on unusual vessel config
};

/**
 * WBC3 Compliant Drill Requirements Matrix
 * Based on Regulations GPT validation and UK Workboat Code Edition 3
 * 
 * CRITICAL: This matrix only applies to WORKBOATS under WBC3
 * Passenger vessels use different requirements under MSN 1823
 */
export const WBC3_DRILL_MATRIX = {
  [DRILL_TYPES.FIRE]: {
    getRequirement: (vesselData) => {
      // MANDATORY MONTHLY per SI 1999/2722 Regulation 8(2)
      // NO exceptions for crew size
      return {
        drill_type: 'fire',
        frequency_days: COMPLIANCE_CONSTANTS.DRILLS.FIRE_DRILL_DAYS,
        compliance_level: COMPLIANCE_LEVELS.REQUIRED,
        risk_dependent: false,
        regulatory_source: 'SI 1999/2722 Regulation 8(2)',
        note: 'Monthly drills required by law - each crew member must participate at least once per month',
        configurable: false,
        warning: '⚠️ MANDATORY: Fire drills must be conducted monthly per UK regulations. NO exceptions for crew size.'
      };
    },
    applicableFor: () => true // Required for all vessels
  },

  [DRILL_TYPES.ABANDON_SHIP]: {
    getRequirement: (vesselData) => {
      // MANDATORY MONTHLY per SI 1999/2722 Regulation 8(2)
      // NO exceptions for crew size
      return {
        drill_type: 'abandon_ship',
        frequency_days: COMPLIANCE_CONSTANTS.DRILLS.ABANDON_SHIP_DAYS, // Now correctly set to 30 days
        compliance_level: COMPLIANCE_LEVELS.REQUIRED,
        risk_dependent: false,
        regulatory_source: 'SI 1999/2722 Regulation 8(2)',
        note: 'Monthly drills required by law - each crew member must participate at least once per month',
        configurable: false,
        warning: '⚠️ MANDATORY: Abandon ship drills must be conducted monthly (30 days) per UK regulations. NO exceptions for crew size.'
      };
    },
    applicableFor: () => true // Required for all vessels
  },

  [DRILL_TYPES.MOB]: {
    getRequirement: (vesselData) => {
      // MOB drills "regularly routinely" per WBC3 Section 14.11.2
      // Industry practice interprets this as quarterly (90 days)
      return {
        drill_type: 'mob',
        frequency_days: COMPLIANCE_CONSTANTS.DRILLS.MAN_OVERBOARD_DAYS,
        compliance_level: COMPLIANCE_LEVELS.REQUIRED,
        risk_dependent: false,
        regulatory_source: 'WBC3 Section 14.11.2',
        note: 'Required "regularly routinely" - industry practice is quarterly (90 days)',
        configurable: true,
        warning: 'Frequency based on industry practice and risk assessment. May be adjusted based on vessel operations.'
      };
    },
    applicableFor: () => true // Always include MOB drill for all vessels
  },

  [DRILL_TYPES.POLLUTION_RESPONSE]: {
    getRequirement: (vesselData) => {
      const { SOPEP_applicable, oil_capacity_litres } = vesselData;
      const requiresSOPEP = SOPEP_applicable || (oil_capacity_litres && oil_capacity_litres > 400);
      
      if (requiresSOPEP) {
        return {
          drill_type: 'pollution_response',
          frequency_days: 180,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: 'MARPOL, SOPEP, WBC3 Section 10.1.10',
          note: 'Recommended if SOPEP applicable or oil capacity >400L'
        };
      } else {
        return {
          drill_type: 'pollution_response',
          frequency_days: 180,
          compliance_level: COMPLIANCE_LEVELS.OPTIONAL,
          risk_dependent: true,
          regulatory_source: 'WBC3 Section 10.1.10 + MARPOL',
          note: 'Optional - no oil capacity or SOPEP requirement'
        };
      }
    },
    applicableFor: (vesselData) => {
      // Only show if vessel actually needs it (SOPEP or oil handling)
      const { SOPEP_applicable, oil_capacity_litres } = vesselData;
      return SOPEP_applicable || (oil_capacity_litres && oil_capacity_litres > 400);
    }
  },

  [DRILL_TYPES.GMDSS_DSC]: {
    getRequirement: (vesselData) => {
      return {
        drill_type: 'gmdss_dsc',
        frequency_days: COMPLIANCE_CONSTANTS.DRILLS.MAN_OVERBOARD_DAYS,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'MGN 683, ISM Section 8',
        note: 'Recommended for GMDSS-equipped vessels or Cat 0-3'
      };
    },
    applicableFor: (vesselData) => {
      const { GMDSS_equipped, vessel_category } = vesselData;
      const requiresGMDSS = ['Cat 0', 'Cat 1', 'Cat 2', 'Cat 3'].includes(vessel_category);
      return GMDSS_equipped || requiresGMDSS;
    }
  },

  [DRILL_TYPES.MEDICAL_EMERGENCY]: {
    getRequirement: (vesselData) => {
      const { medical_stores_category, passenger_count, SOLAS_applicable, vessel_type } = vesselData;
      const hasMedicalStores = ['A', 'B'].includes(medical_stores_category);
      
      // High-risk vessel types requiring enhanced medical preparedness
      const highRiskTypes = ['safety_standby', 'dive_support', 'dredger'];
      const isHighRiskVessel = highRiskTypes.includes(normalizeVesselType(vessel_type));
      
      // Enhanced frequency for SOLAS vessels or high-risk operations
      if (SOLAS_applicable || isHighRiskVessel) {
        return {
          drill_type: 'medical_emergency',
          frequency_days: COMPLIANCE_CONSTANTS.DRILLS.MAN_OVERBOARD_DAYS,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: SOLAS_applicable ? 'WBC3 Section 10.1.10 / SOLAS' : 'WBC3 Section 10.1.10 / High-Risk Operations',
          note: `Recommended - Enhanced frequency for ${SOLAS_applicable ? 'SOLAS' : 'high-risk'} operations`
        };
      }
      
      if (hasMedicalStores) {
        return {
          drill_type: 'medical_emergency',
          frequency_days: 180,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: 'SCMG 24th Ed',
          note: 'Recommended for vessels with Category A or B medical stores'
        };
      } else if (passenger_count > 12) {
        return {
          drill_type: 'medical_emergency',
          frequency_days: 180,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: 'SCMG, WBC3 Section 10.1.10',
          note: 'Recommended for passenger vessels - risk assessment dependent'
        };
      } else {
        return {
          drill_type: 'medical_emergency',
          frequency_days: 180,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: 'Best Practice',
          note: 'Risk assessment dependent'
        };
      }
    },
    applicableFor: (vesselData) => {
      const { medical_stores_category, passenger_count, SOLAS_applicable, vessel_type } = vesselData;
      const hasMedicalStores = ['A', 'B'].includes(medical_stores_category);
      const highRiskTypes = ['safety_standby', 'dive_support', 'dredger'];
      const isHighRiskVessel = highRiskTypes.includes(normalizeVesselType(vessel_type));
      
      return hasMedicalStores || passenger_count > 12 || SOLAS_applicable || isHighRiskVessel || vesselData.vessel_category !== 'Cat 6';
    }
  },

  [DRILL_TYPES.FLOODING]: {
    getRequirement: (vesselData) => {
      const { SOLAS_applicable, vessel_type } = vesselData;
      
      // High-risk vessel types with enhanced flooding risk
      const highRiskTypes = ['safety_standby', 'dive_support', 'dredger'];
      const isHighRiskVessel = highRiskTypes.includes(normalizeVesselType(vessel_type));
      
      // Enhanced frequency for SOLAS vessels or high-risk operations
      if (SOLAS_applicable || isHighRiskVessel) {
        return {
          drill_type: 'flooding',
          frequency_days: COMPLIANCE_CONSTANTS.DRILLS.MAN_OVERBOARD_DAYS,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: SOLAS_applicable ? 'WBC3 Section 10.1.10 / SOLAS' : 'WBC3 Section 10.1.10 / High-Risk Operations',
          note: `Enhanced frequency for ${SOLAS_applicable ? 'SOLAS' : 'high-risk'} operations with flooding risk`
        };
      }
      
      return {
        drill_type: 'flooding',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'WBC3 Section 10.1.10',
        note: 'As per vessel\'s risk assessment'
      };
    },
    applicableFor: (vesselData) => {
      const { vessel_category, length, SOLAS_applicable, vessel_type } = vesselData;
      const isLargeVessel = length && length > 10;
      const isOffshore = ['Cat 0', 'Cat 1', 'Cat 2'].includes(vessel_category);
      const highRiskTypes = ['safety_standby', 'dive_support', 'dredger'];
      const isHighRiskVessel = highRiskTypes.includes(normalizeVesselType(vessel_type));
      
      return isLargeVessel || isOffshore || SOLAS_applicable || isHighRiskVessel;
    }
  },

  [DRILL_TYPES.STEERING_NAV_FAIL]: {
    getRequirement: (vesselData) => {
      const { SOLAS_applicable, emergency_steering } = vesselData;
      
      // 1. SOLAS-applicable vessels: 90 days, Recommended
      if (SOLAS_applicable) {
        return {
          drill_type: 'steering_nav_fail',
          frequency_days: COMPLIANCE_CONSTANTS.DRILLS.MAN_OVERBOARD_DAYS,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: 'SOLAS Chapter II-1 Regulation 29.3',
          note: 'Recommended for SOLAS vessels with emergency steering systems'
        };
      }
      
      // 2. Non-SOLAS vessels with emergency steering arrangements: 180 days, Recommended
      if (emergency_steering) {
        return {
          drill_type: 'steering_nav_fail',
          frequency_days: 180,
          compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
          risk_dependent: true,
          regulatory_source: 'Workboat Code Edition 3 Section 10.1.10',
          note: 'Recommended for vessels with emergency steering arrangements'
        };
      }
      
      // Should not reach here due to applicableFor logic, but provide fallback
      return {
        drill_type: 'steering_nav_fail',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'Best Practice',
        note: 'Risk assessment dependent'
      };
    },
    applicableFor: (vesselData) => {
      const { SOLAS_applicable, emergency_steering } = vesselData;
      
      // Only show if vessel is SOLAS-applicable OR has emergency steering arrangements
      // Non-SOLAS vessels without emergency steering: Not Applicable (drill not shown)
      return SOLAS_applicable || emergency_steering;
    }
  },

  [DRILL_TYPES.ELECTRICAL_BLACKOUT]: {
    getRequirement: (vesselData) => {
      return {
        drill_type: 'electrical_blackout',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'WBC3 Section 9.7 / Best Practice',
        note: 'Required if centralised electrical system, emergency generator, or DP systems'
      };
    },
    applicableFor: (vesselData) => {
      // Check for specific electrical systems that would trigger this drill
      const hasCentralizedElectrical = vesselData.centralizedElectrical || vesselData.power_critical_systems;
      const hasEmergencyGenerator = vesselData.emergencyGenerator || vesselData.hasEmergencyGenerator;
      const hasDPSystems = vesselData.dpSystem || vesselData.dynamicPositioning || 
                          (vesselData.optionalEquipment && vesselData.optionalEquipment.includes('dynamic_positioning'));
      const hasElectricPropulsion = vesselData.electricPropulsion || vesselData.hybridPropulsion;
      const hasBatteryBackup = vesselData.batteryBackup || vesselData.navigationBatteryBackup;
      const hasBowThruster = vesselData.bowThruster || 
                            (vesselData.optionalEquipment && vesselData.optionalEquipment.includes('bow_thruster'));
      
      // Return true if any electrical dependency exists
      return hasCentralizedElectrical || hasEmergencyGenerator || hasDPSystems || 
             hasElectricPropulsion || hasBatteryBackup || hasBowThruster;
    }
  },

  [DRILL_TYPES.ENCLOSED_SPACE_ENTRY]: {
    getRequirement: (vesselData) => {
      return {
        drill_type: 'enclosed_space_entry',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'Best Practice',
        note: 'As per vessel\'s risk assessment'
      };
    },
    applicableFor: (vesselData) => {
      return vesselData.enclosed_spaces_present === true;
    }
  },

  [DRILL_TYPES.TOWING_EMERGENCY]: {
    getRequirement: (vesselData) => {
      return {
        drill_type: 'towing_emergency',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'Best Practice',
        note: 'As per vessel\'s risk assessment'
      };
    },
    applicableFor: (vesselData) => {
      const towingTypes = ['tug', 'utility', 'multipurpose', 'support'];
      return vesselData.towing_capable === true || 
             towingTypes.includes(normalizeVesselType(vesselData.vessel_type));
    }
  },

  [DRILL_TYPES.COLLISION]: {
    getRequirement: (vesselData) => {
      return {
        drill_type: 'collision',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'WBC3 Section 10.1.10 + SMS best practice',
        note: 'Recommended for vessels with collision risk exposure'
      };
    },
    applicableFor: (vesselData) => {
      const { vessel_category, vessel_type } = vesselData;
      const isOffshore = ['Cat 0', 'Cat 1', 'Cat 2', 'Cat 3'].includes(vessel_category);
      const highRiskTypes = ['cargo', 'tug', 'dredger', 'multipurpose'];
      const isHighRiskType = highRiskTypes.includes(normalizeVesselType(vessel_type));
      
      return isOffshore || isHighRiskType;
    }
  },

  [DRILL_TYPES.GROUNDING]: {
    getRequirement: (vesselData) => {
      return {
        drill_type: 'grounding',
        frequency_days: 180,
        compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
        risk_dependent: true,
        regulatory_source: 'WBC3 Section 10.1.10 + SMS best practice',
        note: 'Recommended for vessels operating in shallow or constrained waters'
      };
    },
    applicableFor: (vesselData) => {
      const { vessel_category, vessel_type } = vesselData;
      const isOffshore = ['Cat 0', 'Cat 1', 'Cat 2', 'Cat 3'].includes(vessel_category);
      const groundingRiskTypes = ['cargo', 'tug', 'dredger', 'multipurpose', 'pilot'];
      const isGroundingRiskType = groundingRiskTypes.includes(normalizeVesselType(vessel_type));
      
      return isOffshore || isGroundingRiskType;
    }
  }
};

/**
 * Get drill requirements based on vessel type and applicable regulations
 */
export const getDrillRequirements = (vesselConfig) => {
  // First determine which regulatory framework applies
  const framework = determineRegulatoryFramework(vesselConfig);
  
  // Check if this is an inland waterways vessel
  if (vesselConfig.operatingStatus === 'inland') {
    return getInlandDrillRequirements(vesselConfig, framework);
  }
  
  // CRITICAL: Check if this is a passenger vessel
  if (normalizeVesselType(vesselConfig.type) === 'pax' || normalizeVesselType(vesselConfig.type) === 'passenger') {
    // Passenger vessels have different requirements - NOT WBC3
    return getPassengerVesselDrillRequirements(vesselConfig, framework);
  }
  
  // For workboats, use the existing WBC3 matrix
  const vesselData = {
    crew_count: vesselConfig.crewCount || vesselConfig.crew_count || 3,
    passenger_count: vesselConfig.passengerCapacity || vesselConfig.passenger_count || 0,
    vessel_category: `Cat ${vesselConfig.category}`,
    vessel_type: vesselConfig.type || vesselConfig.vesselType,
    SOLAS_applicable: vesselConfig.SOLAS_applicable || false,
    GMDSS_equipped: vesselConfig.GMDSS_equipped || (vesselConfig.category <= 3),
    SOPEP_applicable: vesselConfig.SOPEP_applicable || false,
    oil_capacity_litres: vesselConfig.oil_capacity_litres || vesselConfig.oilCapacity || 0,
    medical_stores_category: vesselConfig.medical_stores_category || vesselConfig.medicalCategory || 'none',
    power_critical_systems: vesselConfig.power_critical_systems || false,
    enclosed_spaces_present: vesselConfig.enclosed_spaces_present || vesselConfig.hasEnclosedSpaces || false,
    towing_capable: vesselConfig.towing_capable || (normalizeVesselType(vesselConfig.type || vesselConfig.vesselType) === 'tug'),
    emergency_steering: vesselConfig.emergency_steering || vesselConfig.hasEmergencySteering || vesselConfig.SOLAS_applicable || false,
    emergencyGenerator: vesselConfig.emergencyGenerator || false,
    centralizedElectrical: vesselConfig.power_critical_systems || false,
    optionalEquipment: vesselConfig.optionalEquipment || [],
    policy_overrides: vesselConfig.policy_overrides !== false,
    length: vesselConfig.length || vesselConfig.lengthOverall || 0
  };

  const requirements = {};

  // Process each drill type through the WBC3 matrix
  Object.entries(WBC3_DRILL_MATRIX).forEach(([drillType, config]) => {
    // Check if this drill is marked as company required
    const isCompanyRequired = vesselConfig.companyRequiredDrills?.includes(drillType);
    
    // If company required, override the applicableFor check
    if (isCompanyRequired || config.applicableFor(vesselData)) {
      const requirement = config.getRequirement(vesselData);
      
      // If it's company required and currently recommended, upgrade it to required
      if (isCompanyRequired && requirement.compliance_level === COMPLIANCE_LEVELS.RECOMMENDED) {
        requirements[drillType] = {
          ...requirement,
          compliance_level: COMPLIANCE_LEVELS.REQUIRED,
          regulatory_source: requirement.regulatory_source + ' + Company SMS Policy',
          note: requirement.note + ' (Company Required)'
        };
      } else {
        requirements[drillType] = requirement;
      }
    }
  });

  // Add company custom drills
  if (vesselConfig.companyCustomDrills && vesselConfig.companyCustomDrills.length > 0) {
    vesselConfig.companyCustomDrills.forEach(customDrill => {
      requirements[customDrill.id] = {
        drill_type: customDrill.id,
        frequency_days: customDrill.frequency_days,
        compliance_level: COMPLIANCE_LEVELS.REQUIRED, // Custom drills are treated as Company Required
        risk_dependent: false,
        regulatory_source: 'Company SMS Policy',
        note: `Custom drill: ${customDrill.name}`,
        configurable: true,
        custom: true,
        customName: customDrill.name
      };
    });
  }

  return requirements;
};

/**
 * Get drill requirements for inland waterways vessels
 * 
 * IMPORTANT: SI 1999/2722 (mandatory drills) applies to "sea-going" vessels ONLY
 * Inland waterways vessels have NO regulatory drill requirements
 * All drills listed are OPTIONAL company best practices
 */
const getInlandDrillRequirements = (vesselConfig, framework) => {
  const requirements = {};
  const vesselType = normalizeVesselType(vesselConfig.type || vesselConfig.vesselType);
  
  // Special handling for hire boats - NO DRILLS by default
  if (vesselType === 'hire_boat' || vesselType === 'hire boat') {
    // Hire boats: No drills unless company specifically adds them
    // Focus is on customer safety briefings at handover
    return requirements; // Return empty - no drills
  }
  
  // Determine drill profile based on vessel type
  const isPassengerVessel = vesselType === 'pax' || vesselType === 'passenger' || 
                           vesselConfig.passengerCapacity > 0;
  const isCommercial = vesselConfig.commercialOperations !== false && 
                      vesselType !== 'private' && vesselType !== 'leisure';
  
  // Fire drills - OPTIONAL best practice (no regulatory requirement)
  if (isCommercial || isPassengerVessel) {
    requirements.fire = {
      drill_type: 'fire',
      frequency_days: 30,
      compliance_level: COMPLIANCE_LEVELS.OPTIONAL,
      risk_dependent: false,
      regulatory_source: 'Company Policy - No regulatory requirement',
      note: 'Optional: Monthly fire drills are best practice but NOT required for inland vessels',
      configurable: true
    };
  }
  // Private/leisure vessels - no fire drills by default
  
  // MOB - OPTIONAL best practice (no regulatory requirement)
  if (isCommercial || isPassengerVessel) {
    requirements.mob = {
      drill_type: 'mob',
      frequency_days: 90,
      compliance_level: COMPLIANCE_LEVELS.OPTIONAL,
      risk_dependent: true,
      regulatory_source: 'Company Policy - No regulatory requirement',
      note: 'Optional: Quarterly MOB drills are best practice but NOT required for inland vessels',
      configurable: true
    };
  }
  // Private/leisure vessels - no MOB drills by default
  
  // Abandon ship - Only if lifesaving equipment carried
  const hasLSA = vesselConfig.lifesavingEquipment && 
                vesselConfig.lifesavingEquipment !== 'none' &&
                vesselConfig.lifesavingEquipment !== 'minimal';
  
  if (hasLSA) {
    requirements.abandon_ship = {
      drill_type: 'abandon_ship',
      frequency_days: 90,
      compliance_level: COMPLIANCE_LEVELS.OPTIONAL,
      risk_dependent: true,
      regulatory_source: 'Company SMS / Risk Assessment',
      note: 'Quarterly abandon ship drills when substantial LSA carried',
      configurable: true
    };
  }
  
  // Medical emergency - ONLY if company chooses to add
  // Not included by default as there's no regulatory basis
  
  // Other drills - ONLY added if company specifically configures them
  // No pollution, collision, or grounding drills by default
  
  // Add informational note about inland waterways drill requirements
  // Only show if vessel has crew (not relevant for unmanned hire boats)
  const hasCrew = vesselConfig.crewCount > 0 || vesselConfig.crew_count > 0;
  
  if (Object.keys(requirements).length === 0 && hasCrew) {
    // No drills configured but vessel has crew - show info
    requirements._info = {
      type: 'info',
      title: 'No Drill Requirements for Inland Waterways',
      message: 'Inland waterways vessels are not subject to SI 1999/2722 mandatory drill requirements. ' +
               'Your company may choose to implement drills as best practice.',
      action: 'To add company-specific drills, go to Vessel Settings > Drill Configuration',
      regulatory_source: 'SI 1999/2722 applies to sea-going vessels only'
    };
  } else if (Object.keys(requirements).length > 0) {
    // Some drills configured - add clarification
    requirements._info = {
      type: 'info',
      title: 'Optional Drills Only',
      message: 'These drills are company best practices, not regulatory requirements.',
      regulatory_source: 'No mandatory drills for inland waterways vessels'
    };
  }
  // No info for hire boats with no crew - they understand no drills needed
  
  // Apply company-required drill overrides
  if (vesselConfig.companyRequiredDrills) {
    vesselConfig.companyRequiredDrills.forEach(drillType => {
      if (requirements[drillType]) {
        requirements[drillType].compliance_level = COMPLIANCE_LEVELS.REQUIRED;
        requirements[drillType].regulatory_source += ' + Company Policy';
        requirements[drillType].note += ' (Company Required)';
        requirements[drillType].configurable = false;
      }
    });
  }
  
  // Add custom company drills
  if (vesselConfig.companyCustomDrills && vesselConfig.companyCustomDrills.length > 0) {
    vesselConfig.companyCustomDrills.forEach(customDrill => {
      requirements[customDrill.id] = {
        drill_type: customDrill.id,
        frequency_days: customDrill.frequency_days,
        compliance_level: COMPLIANCE_LEVELS.REQUIRED,
        risk_dependent: false,
        regulatory_source: 'Company SMS Policy',
        note: `Custom drill: ${customDrill.name}`,
        configurable: true,
        custom: true,
        customName: customDrill.name
      };
    });
  }
  
  return requirements;
};

/**
 * Get drill requirements for passenger vessels
 */
const getPassengerVesselDrillRequirements = (vesselConfig, framework) => {
  const requirements = {};
  
  // Fire and Abandon Ship - ALWAYS monthly for ALL vessels
  requirements.fire = {
    drill_type: 'fire',
    frequency_days: 30,
    compliance_level: COMPLIANCE_LEVELS.REQUIRED,
    risk_dependent: false,
    regulatory_source: 'SI 1999/2722 Regulation 8(2)',
    note: 'Monthly drills required by law for ALL vessels',
    configurable: false
  };
  
  requirements.abandon_ship = {
    drill_type: 'abandon_ship',
    frequency_days: 30,
    compliance_level: COMPLIANCE_LEVELS.REQUIRED,
    risk_dependent: false,
    regulatory_source: 'SI 1999/2722 Regulation 8(2)',
    note: 'Monthly drills required by law for ALL vessels',
    configurable: false
  };
  
  // MOB - Required but frequency per SMS
  requirements.mob = {
    drill_type: 'mob',
    frequency_days: 90,
    compliance_level: COMPLIANCE_LEVELS.REQUIRED,
    risk_dependent: false,
    regulatory_source: 'Company SMS',
    note: 'Required - frequency per SMS interpretation',
    configurable: true
  };
  
  // Additional drills - ALL are SMS-based, not regulatory
  if (vesselConfig.medical_stores_category && vesselConfig.medical_stores_category !== 'none') {
    requirements.medical_emergency = {
      drill_type: 'medical_emergency',
      frequency_days: 180,
      compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
      risk_dependent: true,
      regulatory_source: 'Company SMS / Risk Assessment',
      note: 'Recommended based on medical stores carried',
      configurable: true
    };
  }
  
  if (vesselConfig.emergency_steering) {
    requirements.steering_nav_fail = {
      drill_type: 'steering_nav_fail',
      frequency_days: 180,
      compliance_level: COMPLIANCE_LEVELS.RECOMMENDED,
      risk_dependent: true,
      regulatory_source: 'Company SMS / Risk Assessment',
      note: 'Recommended for vessels with emergency steering',
      configurable: true
    };
  }
  
  return requirements;
};

/**
 * Get drill frequency in days (legacy compatibility)
 */
export const getDrillFrequencyDays = (drillType, vesselConfig) => {
  const requirements = getDrillRequirements(vesselConfig);
  return requirements[drillType]?.frequency_days || null;
};

/**
 * Check if drill is required for vessel
 */
export const isDrillRequired = (vesselConfig, drillType) => {
  const requirements = getDrillRequirements(vesselConfig);
  return requirements[drillType]?.compliance_level === COMPLIANCE_LEVELS.REQUIRED;
};

/**
 * Get compliance status for drill
 */
export const getDrillComplianceStatus = (vesselConfig, drillType) => {
  const requirements = getDrillRequirements(vesselConfig);
  return requirements[drillType] || null;
};

/**
 * Get all applicable drill types for vessel
 */
export const getApplicableDrillTypes = (vesselConfig) => {
  const requirements = getDrillRequirements(vesselConfig);
  return Object.keys(requirements);
};

/**
 * Get human-readable requirement description
 */
export const getRequirementDescription = (requirement) => {
  if (!requirement) return 'Not Required';
  
  const { frequency_days, compliance_level } = requirement;
  const interval = frequency_days <= 30 ? 'Monthly' : 
                  frequency_days <= 90 ? 'Quarterly' : 'Bi-Annually';
  
  return `${compliance_level} ${interval}`;
};

/**
 * Legacy requirement levels (for backward compatibility)
 */
export const DRILL_REQUIREMENTS = {
  REQUIRED_MONTHLY: 'required_monthly',
  REQUIRED_QUARTERLY: 'required_quarterly', 
  RECOMMENDED_MONTHLY: 'recommended_monthly',
  RECOMMENDED_QUARTERLY: 'recommended_quarterly',
  NOT_REQUIRED: 'not_required'
};

/**
 * Convert new compliance data to legacy format (for existing components)
 */
export const getLegacyRequirement = (requirement) => {
  if (!requirement) return DRILL_REQUIREMENTS.NOT_REQUIRED;
  
  const { frequency_days, compliance_level } = requirement;
  
  if (compliance_level === COMPLIANCE_LEVELS.REQUIRED) {
    return frequency_days <= 30 ? DRILL_REQUIREMENTS.REQUIRED_MONTHLY : DRILL_REQUIREMENTS.REQUIRED_QUARTERLY;
  } else if (compliance_level === COMPLIANCE_LEVELS.RECOMMENDED) {
    return frequency_days <= 30 ? DRILL_REQUIREMENTS.RECOMMENDED_MONTHLY : DRILL_REQUIREMENTS.RECOMMENDED_QUARTERLY;
  }
  
  return DRILL_REQUIREMENTS.NOT_REQUIRED;
};